<template>
  <main class="pt-10">
    <div class="flex items-center gap-x-4 lg:gap-x-12">
      <div
        @click="goBack"
        class="flex items-center border-main rounded-lg shadow-xs w-20 gap-x-2 text-gray-90 border-gray-90 px-1 py-1.5 lg:py-2 font-semibold text-fg-default text-xs cursor-pointer justify-center"
      >
        <i class="el-icon-back"></i>
        <p class="font-light text-xs">Back</p>
      </div>
      <div class="flex items-center gap-x-2 lg:gap-x-4">
        <bulk-payout-icon-2 />
        <span class="text-lg font-light"></span>
        <p class="font-normal text-xs lg:text-sm">New Payout</p>
      </div>
    </div>
    <!-- form -->
    <section class="md:grid md:place-items-center">
      <div class="lg:mt-6 mt-4 lg:w-5/12 2xl:w-4/12">
        <div class="border-main rounded-2xl shadow-xs px-8 py-6">
          <div
            class="flex items-end justify-end w-full"
            @click="openModal('showGuideInstruction')"
          >
            <p
              class="bg-info-subtle px-2 py-2 rounded-lg shadow-xs cursor-pointer flex items-center justify-center gap-x-2.5"
            >
              <question-mark />
              <span class="text-primary text-xs font-normal"
                >See Guide & Instructions</span
              >
            </p>
          </div>
          <el-form
            label-position="top"
            :model="bulkPayoutForm"
            ref="bulkPayoutForm"
            :rules="rules"
            size="normal"
            class="mt-10"
          >
            <el-form-item prop="currency" id="bulkPayoutTourItem2">
              <p class="flex items-center gap-x-1 mb-3">
                <span class="text-gray-muted font-medium text-sm"
                  >Payout Currency</span
                >
                <tooltip
                  :disabled="false"
                  :content="tooltipContent"
                  placement="top"
                >
                  <span>
                    <info-icon size="h-5 w-5" />
                  </span>
                </tooltip>
              </p>
              <div class="relative">
                <p
                  class="flex items-center gap-x-1 font-normal lg:gap-x-2 absolute left-4 top-2 z-10"
                >
                  <currency-icons
                    size="lg:h-8 lg:w-8 h-5 w-5"
                    :currency="getCurrencyCountry(bulkPayoutForm.currency)"
                  />
                  <span class="text-black-10">{{
                    bulkPayoutForm.currency
                  }}</span>
                  <span class="text-gray-muted"
                    >Bal: {{ formatHoldingsBalance }}</span
                  >
                </p>

                <el-select
                  id="select"
                  v-model="bulkPayoutForm.currency"
                  @change="handleCurrencyChange"
                >
                  <el-option
                    v-for="(account, index) in totalAccounts"
                    :value="account.currency"
                    :key="index"
                    style="margin-bottom: 0.5rem"
                    :disabled="disablePayoutForm(account)"
                    :class="{ 'opacity-50': !PayoutCurrency.includes(account) }"
                  >
                    <div class="flex items-center gap-x-2">
                      <currency-icons
                        :currency="getCurrencyCountry(account.currency)"
                      />
                      <span>{{ account.currency }}</span>
                      <span
                        >Bal:
                        {{
                          parseMoneyAll(
                            account.currentBalance,
                            account.currency
                          )
                        }}</span
                      >
                    </div>
                  </el-option>
                </el-select>
              </div>
            </el-form-item>

            <div v-if="hasPaymentMethods">
              <el-form-item label="Payment Method">
                <el-select
                  v-model="bulkPayoutForm.paymentMethod"
                  @change="handlePaymentMethod"
                >
                  <el-option
                    v-for="(paymentMethod, index) in paymentMethods"
                    :key="index"
                    :label="paymentMethod.label"
                    :value="paymentMethod.value"
                    :disabled="shouldDisablePaymentMethod(paymentMethod)"
                  >
                    {{ paymentMethod.label }}
                  </el-option>
                </el-select>
              </el-form-item>
            </div>
            <el-form-item prop="beneficiary_type" id="bulkPayoutTourItem3">
              <p class="font-normal text-fg-muted flex items-center gap-x-1">
                <span class="small-text">Beneficiary Type</span>
              </p>
              <el-select
                v-model="bulkPayoutForm.beneficiary_type"
                @change="handleBeneficiaryTypeChange"
              >
                <el-option
                  v-for="(beneficiaryType, index) in beneficiaryTypes"
                  :key="index"
                  :label="beneficiaryType.label"
                  :value="beneficiaryType.value"
                >
                  {{ beneficiaryType.label }}
                </el-option>
              </el-select>
            </el-form-item>
            <div
              v-if="showSelectBeneficiaryBtn"
              class="cursor-pointer flex flex-col w-full items-center rounded-md border border-dashed py-5"
              @click="openModal('showBeneficiariesDrawer')"
            >
              <p class="font-normal">Select Beneficiary</p>
              <p class="font-light text-xs text-fg-muted mt-2">
                + Click to Add
              </p>
            </div>
            <div v-if="displaySelectedBeneficiary">
              <div
                class="border border-root-2 p-4 rounded-lg mt-4 cursor-pointer backgroundColor"
              >
                <div class="flex flex-col items-start relative">
                  <div class="grid grid-cols-12 gap-3">
                    <el-avatar
                      class="col-span-2 bg-subtle text-primary border-main lg:text-xl flex items-center justify-center"
                      >{{
                        beneficiaryBankDetailsPointer.accountName
                          .charAt(0)
                          .toUpperCase()
                      }}</el-avatar
                    >
                    <div
                      class="col-span-10 flex items-center justify-between w-full capitalize"
                    >
                      <div>
                        <p class="text-black-10 font-bold text-base capitalize">
                          {{
                            beneficiaryBankDetailsPointer.accountName ||
                            beneficiaryBankDetailsPointer.name ||
                            '-'
                          }}
                        </p>
                        <p
                          class="text-black-2 text-sm font-normal capitalize my-1"
                        >
                          {{ accountProviderTitle }}:
                          {{ beneficiaryBankDetailsPointer.bankName || '-' }}
                        </p>
                        <p class="text-black-2 text-sm font-normal">
                          {{ accountTitle }}:
                          {{
                            beneficiaryBankDetailsPointer.accountNumber ||
                            beneficiaryBankDetailsPointer.accountNo ||
                            '-'
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div
                    class="ml-auto absolute right-2 top-0"
                    @click="clearSelectedBeneficiary"
                  >
                    <close-icon class="close" />
                  </div>
                </div>
                <!-- intermediary bank details preview -->
                <section v-if="selectedBeneficiary.intermediaryBank">
                  <hr class="my-2" />
                  <p class="my-2 font-normal text-sm lg:text-base">
                    Intermediary Bank Details
                  </p>
                  <p class="text-black-2 text-sm font-normal mb-1">
                    Bank:
                    {{ selectedBeneficiary.intermediaryBank.bankName }}
                  </p>
                  <p class="text-black-2 text-sm font-normal">
                    Swift Code:
                    {{ selectedBeneficiary.intermediaryBank.swiftCode }}
                  </p>
                </section>
              </div>
            </div>

            <div
              v-if="bulkPayoutForm.beneficiary_type === 'multiple'"
              class="border border-gray-120 text-xs sm:text-sm font-normal rounded-bl-xl rounded-br-xl px-4 py-5 mb-2"
            >
              <div
                class="flex flex-col gap-2"
                v-if="selectedBeneficiaries.length > 0"
              >
                <p class="text-input-text">Beneficiary List</p>

                <div
                  class="h-12 w-full flex items-center justify-between px-4 rounded-lg border border-gray-120"
                >
                  <span
                    >{{ selectedBeneficiaries.length }} Beneficiaries
                    selected</span
                  >

                  <span @click="goToSelectBeneficiary" class="cursor-pointer">
                    <edit-icon />
                  </span>
                </div>

                <p
                  @click="clearSavedBeneficiaries"
                  class="mt-1 text-primary cursor-pointer underline hover:opacity-80"
                >
                  Clear list
                </p>
              </div>

              <div v-else>
                <div v-if="showUploadList">
                  <div class="flex items-center justify-between mb-2">
                    <div class="flex items-center gap-1">
                      <p class="text-input-text">Upload beneficiary list</p>
                      <el-tooltip
                        content="Download the CSV file which contains a template for beneficiary information. Fill in the beficiary list, save it and upload it"
                        placement="top"
                      >
                        <span>
                          <info-icon size="h-5 w-5" />
                        </span>
                      </el-tooltip>
                    </div>

                    <!-- The id in the next line maps the walkthrough to that p element. Please do not remove this id -->
                    <p class="flex items-center gap-x-2" id="bulkPayoutItem3">
                      <a
                        class="text-primary cursor-pointer underline"
                        target="_blank"
                        rel="noopener noreferrer"
                        :href="sampleDocumentUrl"
                        download
                      >
                        Download Sample CSV
                      </a>
                      <download-icon-2 />
                    </p>
                  </div>

                  <!-- The id in the next line maps the walkthrough to that el-form-item element. Please do not remove this id -->
                  <el-form-item prop="document">
                    <el-input
                      type="file"
                      name="documentFile"
                      accept=".xlsx"
                      placeholder="Upload Document"
                      v-model="bulkPayoutForm.document"
                      @change="handleChange"
                      id="fileInput"
                    >
                      <i
                        slot="prefix"
                        class="text-fg-muted absolute top-1 left-2 overflow-x-scroll truncate w-16 lg:w-24"
                        >{{ fileName }}</i
                      >
                      <i
                        slot="suffix"
                        class="flex items-center justify-center w-28 h-10 absolute right-0 top-2 cursor-pointer fg-on-disabled rounded-lg -z-10 browseButton"
                      >
                        <span class="text-white text-xs mr-2 ml-2"
                          >Choose file</span
                        >
                      </i></el-input
                    >
                  </el-form-item>

                  <p
                    @click="toggleUploadType"
                    class="-mt-2 text-primary underline cursor-pointer"
                  >
                    Select from Save Beneficiaries
                  </p>
                </div>

                <div v-else class="flex flex-col gap-2">
                  <div class="flex items-center gap-1">
                    <p class="text-input-text">
                      Select from saved Beneficiary ?
                    </p>
                    <el-tooltip
                      content="Select from saved beneficiaries or Add new beneficiaries"
                      placement="top"
                    >
                      <span>
                        <info-icon size="h-5 w-5" />
                      </span>
                    </el-tooltip>
                  </div>

                  <!-- The id in the next line maps the walkthrough to that el-form-item element. Please do not remove this id -->
                  <el-form-item prop="multiBeneficiary">
                    <el-input
                      id="fileInput"
                      v-model="bulkPayoutForm.multiBeneficiaries"
                    >
                      <i
                        slot="prefix"
                        class="text-gray-130 h-full flex items-center pl-2 not-italic"
                      >
                        Select Beneficiaries
                      </i>
                      <i slot="suffix" class="h-full flex items-center pr-1">
                        <button
                          type="button"
                          @click="goToSelectBeneficiary"
                          class="text-primary bg-secondary-2 text-xs p-3 rounded-lg hover:opacity-80"
                        >
                          Select Beneficiary
                        </button>
                      </i>
                    </el-input>
                  </el-form-item>

                  <p
                    @click="toggleUploadType"
                    class="-mt-4 text-primary underline cursor-pointer"
                  >
                    Upload Beneficiary List Instead
                  </p>
                </div>
              </div>
            </div>

            <!-- The id in the next line maps the walkthrough to that el-form-item element. Please do not remove this id -->
            <el-form-item
              label="Payout Tag"
              prop="remarks"
              id="bulkPayoutTourItem4"
            >
              <el-input
                v-model="bulkPayoutForm.remarks"
                placeholder="Enter payout tag"
              />
            </el-form-item>
            <div
              class="text-sm font-normal"
              v-if="isDevEnvironment && isSingleBeneficiary"
            >
              <div class="flex items-center gap-1 mb-2" v-if="invoiceFileName">
                <p class="text-input-text upload-invoice-label">
                  Upload Invoice
                </p>
              </div>
              <el-form-item
                v-if="!invoiceFileName"
                prop="invoice"
                label="Upload Invoice"
                class="relative"
              >
                <el-input
                  type="file"
                  name="documentFile"
                  accept=".jpg, .pdf, .docx, .xlsx, .png, .jpeg"
                  v-model="bulkPayoutForm.invoice"
                  @change="handleInvoiceChange"
                  id="invoiceInput"
                >
                  <i
                    slot="prefix"
                    class="text-fg-muted absolute top-1 left-2 overflow-x-scroll truncate w-16 lg:w-24"
                    >{{ invoiceFileName }}</i
                  >
                  <i
                    slot="suffix"
                    class="flex items-center justify-center w-28 h-10 absolute right-0 top-2 cursor-pointer fg-on-disabled rounded-lg -z-10 browseButton"
                  >
                    <span class="text-white text-xs mr-2 ml-2"
                      >Choose file</span
                    >
                  </i></el-input
                >
                <div
                  v-if="!uploadedFileUrls.length"
                  class="absolute left-0 top-3 right-0 bottom-0 px-4 py-2 text-fg-default opacity-30 text-xs lg:text-sm"
                >
                  PDF, DOC, JPG, XLS, PNG or JPEG
                </div>
              </el-form-item>
              <!-- active state -->
              <div
                v-else
                class="w-full rounded-lg shadow-xs px-2 py-1 border flex items-center justify-between"
              >
                <div class="flex items-center gap-x-1">
                  <span class="relative">
                    <document-icon-2 />
                    <span
                      class="uppercase rounded bg-primary text-white font-semibold text-xs px-1 py-0.5 absolute left-0.5 top-5 transform scale-90"
                      >{{ selectedFileType(fileSelector.files[0]) }}</span
                    >
                  </span>
                  <span class="flex flex-col text-sm">
                    <span
                      class="overflow-x-scroll text-black truncate w-16 lg:w-24"
                    >
                      {{ fileSelector.files[0].name }}
                    </span>
                    <span class="text-fg-muted text-xs">
                      {{ selectedFileSize(fileSelector.files[0]) }}
                    </span>
                  </span>
                </div>
                <delete-icon-2
                  class="cursor-pointer"
                  @click.native="() => removeSelectedInvoice()"
                />
              </div>
            </div>
            <div
              class="text-sm font-normal"
              v-if="isDevEnvironment && isMultipleBeneficiaries"
            >
              <div
                class="flex justify-end items-center gap-1 -mb-8"
                v-if="invoiceFileName"
              >
                <delete-icon-2
                  class="cursor-pointer mr-1 relative z-30"
                  @click.native="() => removeSelectedInvoice()"
                />
              </div>
              <el-form-item
                prop="invoice"
                label="Upload Invoice(s)"
                class="relative"
              >
                <el-input
                  v-if="!uploadedFileUrls.length"
                  type="file"
                  name="documentFile"
                  accept=".jpg, .pdf, .docx, .xlsx, .png, .jpeg"
                  multiple
                  v-model="bulkPayoutForm.invoice"
                  @change="handleInvoiceChange"
                  id="invoiceInput"
                >
                  <i
                    slot="suffix"
                    class="flex items-center justify-center w-28 h-10 absolute right-0 top-2 cursor-pointer fg-on-disabled rounded-lg -z-10 browseButton"
                  >
                    <span class="text-white text-xs mr-2 ml-2"
                      >Choose file</span
                    >
                  </i></el-input
                >
                <div
                  v-if="!uploadedFileUrls.length"
                  class="absolute left-0 top-3 right-0 bottom-0 px-4 py-2 text-fg-default opacity-30 text-xs lg:text-sm"
                >
                  PDF, DOC, JPG, XLS, PNG or JPEG
                </div>
              </el-form-item>
              <!-- active state -->
              <div
                v-if="fileSelector && uploadedFileUrls.length === 0"
                class="w-full rounded-b-xl rounded-bl-xl rounded-br-xl shadow-xs py-1 px-2 border flex flex-col -mt-2"
              >
                <div
                  class="flex items-center justify-between mt-2 mb-3 text-xs"
                >
                  <p class="bg-gray-26 rounded p-1">
                    {{ fileSelector.files.length }} File{{
                      fileSelector.files.length > 1 ? 's' : ''
                    }}
                    Selected
                  </p>
                  <p
                    class="bg-secondary-2 text-primary p-2 rounded cursor-pointer"
                    @click="handleUploadFile"
                  >
                    Upload Selected
                  </p>
                </div>
                <div class="h-24 overflow-y-scroll">
                  <div
                    class="flex items-center justify-between gap-x-1 w-full mb-2 bg-gray-26 px-2 rounded"
                    v-for="(file, index) in fileSelector.files"
                    :key="index"
                  >
                    <div class="flex items-center gap-x-1 w-full transform">
                      <span class="relative transform scale-90">
                        <document-icon-2 size="h-10 w-8" />
                        <span
                          class="uppercase rounded bg-primary text-white font-semibold px-1 absolute -left-0.5 top-3 transform scale-90 text-xxs"
                          >{{ selectedFileType(file) }}</span
                        >
                      </span>
                      <span class="flex flex-col text-xxs">
                        <span
                          class="overflow-x-scroll text-black truncate w-full"
                        >
                          {{ file.name }}
                        </span>
                        <span class="text-fg-muted text-xxs -mt-0.5">
                          {{ selectedFileSize(file) }}
                        </span>
                      </span>
                    </div>
                    <delete-icon-2
                      class="cursor-pointer h-4 w-4 mr-3"
                      @click.native="removeSelectedInvoice(index)"
                    />
                  </div>
                </div>
              </div>
              <div
                class="w-full rounded-lg shadow-xs px-3 py-1.5 border flex items-center gap-x-3 -mt-4"
                v-if="uploadedFileUrls.length"
              >
                <span class="relative">
                  <document-icon-2 />
                  <span
                    class="uppercase rounded bg-primary text-white font-semibold text-xs px-1 py-0.5 absolute left-0.5 top-5 transform scale-90"
                    >{{ firstUploadedFileType }}</span
                  >
                </span>
                <span class="text-fg-muted">{{ firstUploadedFileType }}</span>
                <span>
                  <span v-if="uploadedFileUrls.length > 1"
                    >and {{ uploadedFileUrls.length - 1 }} others</span
                  >
                  Uploaded
                </span>
              </div>
            </div>
            <el-form-item
              v-if="isSingleBeneficiary"
              label="Amount"
              prop="amount"
            >
              <el-input
                v-model="bulkPayoutForm.amount"
                placeholder="Enter Amount"
                @input="handleInput"
              />
            </el-form-item>
            <el-form-item prop="payout_frequency" id="bulkPayoutTourItem5">
              <p class="font-normal text-fg-muted flex items-center gap-x-1">
                <span class="small-text">Select Payout Type</span>
                <el-tooltip :content="payoutTypeTooltipContent" placement="top">
                  <span>
                    <info-icon size="h-5 w-5" />
                  </span>
                </el-tooltip>
              </p>
              <el-select v-model="bulkPayoutForm.payout_frequency">
                <el-option
                  v-for="(payoutType, index) in payoutTypes"
                  :key="index"
                  :label="payoutType.label"
                  :value="payoutType.value"
                  :disabled="payoutTypeDisabled(payoutType.value)"
                >
                  {{ payoutType.label }}
                </el-option>
              </el-select>
            </el-form-item>
            <div v-if="isRecurringPeriod" class="w-full">
              <div class="md:grid md:grid-cols-8 gap-x-2">
                <el-form-item class="col-span-4" label="Recuring Frequency">
                  <el-select
                    @change="handlePeriodChange"
                    v-model="bulkPayoutForm.reccuring_period"
                  >
                    <el-option
                      v-for="(recurringPeriod, index) in recurringPeriods"
                      :key="index"
                      :label="recurringPeriod.label"
                      :value="recurringPeriod.value"
                    >
                      {{ recurringPeriod.label }}
                    </el-option>
                  </el-select>
                </el-form-item>
                <div class="col-span-4">
                  <el-form-item v-if="!isDailyPayout" :label="datePickerLabel">
                    <el-date-picker
                      v-if="datePickerType === 'date'"
                      v-model="bulkPayoutForm.selectedDate"
                      format="dd/MM/yyyy"
                      :type="datePickerType"
                      placeholder="Pick a date"
                    >
                    </el-date-picker>
                    <el-select
                      v-model="bulkPayoutForm.selectedDate"
                      v-if="datePickerType === 'week'"
                    >
                      <el-option
                        v-for="(day, index) in weeklyRecurringPeriods"
                        :key="index"
                        :label="day.label"
                        :value="day.value"
                      >
                        {{ day.label }}
                      </el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item v-else label="Payout End Date (optional)">
                    <el-date-picker
                      v-model="bulkPayoutForm.payoutEndDate"
                      format="d MMM yyyy"
                      type="date"
                      placeholder="Pick a date"
                    >
                    </el-date-picker>
                  </el-form-item>
                </div>
              </div>
              <el-form-item
                v-if="!isDailyPayout"
                label="Payout End Date (optional)"
              >
                <el-date-picker
                  v-model="bulkPayoutForm.payoutEndDate"
                  format="d MMM yyyy"
                  type="date"
                  placeholder="Pick a date"
                >
                </el-date-picker>
              </el-form-item>
            </div>
          </el-form>

          <!-- Button -->
          <div class="mt-5 lg:mt-8">
            <el-tooltip
              :disabled="hasInitiatorAccess"
              content="You do not have the access to initiate a payout"
              placement="top"
            >
              <el-button
                :disabled="isProceedButtonDisabled"
                @click="validateForm('bulkPayoutForm')"
                class="rounded-lg shadow-sm bg-primary text-white py-4 px-8 w-full"
              >
                {{ isLoading ? 'Uploading File...' : 'Proceed' }}
              </el-button>
            </el-tooltip>
          </div>
        </div>
      </div>
    </section>

    <!-- dialogs -->
    <guide-instruction
      v-if="showGuideInstruction"
      :showDialog="showGuideInstruction"
      @closeDialog="closeModal('showGuideInstruction')"
    />
    <validation-dialog
      v-if="showValidationDialog"
      :showDialog="showValidationDialog"
      @closeDialog="closeModal('showValidationDialog')"
    />
    <!-- Drawers -->
    <single-beneficiaries-drawer
      v-if="showBeneficiariesDrawer"
      :showDrawer="showBeneficiariesDrawer"
      :currencyType="bulkPayoutForm.currency"
      :beneficiaryType="bulkPayoutForm.paymentMethod"
      @showAddBeneficiariesDrawer="openModal('showAddBeneficiariesDrawer')"
      @closeDrawer="closeModal('showBeneficiariesDrawer')"
      @selectBeneficiary="selectBeneficiary"
    />
    <add-beneficiary-drawer
      v-if="showAddBeneficiariesDrawer"
      :showDrawer="showAddBeneficiariesDrawer"
      :currency="bulkPayoutForm.currency"
      :paymentType="bulkPayoutForm.paymentMethod"
      @closeDrawer="closeModal('showAddBeneficiariesDrawer')"
    />
  </main>
</template>

<script>
import {
  AddZeroPrefix,
  GetCurrencyCountry,
  ParseMoneyAll,
  ParseAmount,
  RemoveNonDigitAndDecimal,
  FormatAmount,
  GetCurrencyDetails,
  ValidateInput
} from '@/utils/helpers'
import {
  BULK_PAYOUT_PAYMENT_CURRENCIES,
  BULKPAYOUT_DOCUMENT_TEMPLATE_TYPE,
  ENABLED_MULTI_CURRENCY_AND_METHODS,
  FIAT,
  PAYMENT_METHODS,
  PAYOUT_TYPES,
  RECURRING_PERIODS,
  WEEKLY_RECURRING_PERIODS,
  BENEFICIARY_TYPES,
  ACCOUNT_DETAILS_TITLES
} from '@/utils/constants.js'

import getBalance from '@/mixins/getBalance.js'
import { read, utils } from 'xlsx'
import getTransferDetail from '@/mixins/terminal.js'
import userKinds from '@/mixins/userKinds.js'
import provider from '@/mixins/provider.js'

const { sheet_to_json } = utils
export default {
  mixins: [getBalance, getTransferDetail, userKinds, provider],
  components: {
    DeleteIcon2: () => import('@/assets/v2/icons/DeleteIcon2.vue'),
    DocumentIcon2: () => import('@/assets/v2/icons/DocumentIcon2.vue'),
    AddBeneficiaryDrawer: () =>
      import('@/components/v2/bulkPayout/drawers/AddBeneficiaryDrawer.vue'),
    BulkPayoutIcon2: () => import('@/assets/v2/icons/BulkPayoutIcon2.vue'),
    QuestionMark: () => import('@/assets/v2/icons/QuestionMark.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    InfoIcon: () => import('@/assets/v2/icons/InfoIcon.vue'),
    DownloadIcon2: () => import('@/assets/v2/icons/DownloadIcon2.vue'),
    GuideInstruction: () =>
      import('@/components/v2/bulkPayout/dialogs/GuideInstruction.vue'),
    ValidationDialog: () =>
      import('@/components/v2/bulkPayout/dialogs/ValidationDialog.vue'),
    CloseIcon: () => import('@/assets/v2/icons/CloseIcon.vue'),
    EditIcon: () => import('@/assets/v2/icons/EditIcon2.vue'),
    SingleBeneficiariesDrawer: () =>
      import('@/components/v2/bulkPayout/drawers/SingleBeneficiariesDrawer.vue')
  },
  data () {
    return {
      payoutTypes: PAYOUT_TYPES,
      recurringPeriods: RECURRING_PERIODS,
      paymentMethods: PAYMENT_METHODS,
      weeklyRecurringPeriods: WEEKLY_RECURRING_PERIODS,
      showBeneficiariesDrawer: false,
      showAddBeneficiariesDrawer: false,
      beneficiaryTypes: BENEFICIARY_TYPES,
      showGuideInstruction: false,
      filteredData: [],
      parsedXlsxSheetData: [],
      totalAmountSum: 0,
      fileName: '',
      invoiceFileName: '',
      showValidationDialog: false,
      isLoading: false,
      isLoadingFileUpload: false,
      templateCurrency: '',
      showUploadList: false,
      tooltipContent:
        'This would be the currency you want to payout in. The payout amount is debited from your holding account in the same currency you choose.',
      bulkPayoutForm: {
        amount: null,
        currency: '',
        beneficiary_type: '',
        holdingBalance: '',
        document: '',
        remarks: '',
        payout_frequency: '',
        reccuring_period: 'monthly',
        selectedDate: '',
        paymentMethod: '',
        payoutEndDate: '',
        beneficiaryType: 'multiple',
        invoice: null
      },
      scheduleDetails: {},
      payoutTypeTooltipContent:
        'Payout frequency option includes  one time payments and recurring payments is at regular intervals(daily, weekly, or monthly)',
      formData: null,
      rules: {
        paymentMethod: [
          {
            required: true,
            message: 'Please select a payment method',
            trigger: ['blur', 'change']
          }
        ],
        currency: [
          {
            required: true,
            message: 'Please select a payout currency',
            trigger: ['blur', 'change']
          }
        ],
        beneficiary_type: [
          {
            required: true,
            message: 'Please select a beneficiary type',
            trigger: ['blur', 'change']
          }
        ],
        holdingBalance: [
          {
            required: true,
            message: 'Holding balance',
            trigger: ['blur', 'change']
          }
        ],
        amount: [
          {
            required: true,
            validator: this.validateAmount,
            trigger: ['blur', 'change']
          }
        ],
        document: [
          {
            required: true,
            message: 'Please upload your beneficiary list',
            trigger: ['blur', 'change']
          }
        ],
        multiBeneficiary: [
          {
            required: true,
            message: 'Please select multi-beneficiaries',
            trigger: ['blur', 'change']
          }
        ],
        invoice: [
          {
            required: false,
            message: 'Please upload an invoice',
            trigger: ['blur', 'change']
          }
        ],
        remarks: [
          {
            required: true,
            message: 'Please input a tag (or reason) for this payout',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ]
      },
      currencyOptions: ['NGN', 'USD'],
      fileSelector: '',
      uploadedMultipleInvoices: '',
      selectedFilesToUpload: []
    }
  },
  computed: {
    // because of inconsistency in payload
    // todo: refactor to make be and fe payload struct consistent
    beneficiaryBankDetailsPointer () {
      return this.selectedBeneficiary?.bankDetails
        ? this.selectedBeneficiary?.bankDetails
        : this.selectedBeneficiary
    },
    showSelectBeneficiaryBtn () {
      return this.isSingleBeneficiary && !this.selectedBeneficiary
    },
    displaySelectedBeneficiary () {
      return this.isSingleBeneficiary && this.selectedBeneficiary
    },
    hasPayoutEndDate () {
      return this.bulkPayoutForm.payoutEndDate
    },
    payoutEndDateObject () {
      return new Date(this.bulkPayoutForm.payoutEndDate)
    },
    payoutType () {
      const selectedType = BULKPAYOUT_DOCUMENT_TEMPLATE_TYPE.find(
        (type) => type.value === this.bulkPayoutForm.paymentMethod
      )
      return selectedType ? selectedType.value : 'banks' // Default to 'banks' if not found
    },
    hasPaymentMethods () {
      return BULK_PAYOUT_PAYMENT_CURRENCIES.includes(
        this.bulkPayoutForm.currency || ''
      )
    },
    hasNGNPayout () {
      return this.totalAccounts.find(
        (account) => account.currency === this.$CURRENCY.NGN
      )
    },
    hasGHSPayout () {
      return this.totalAccounts.find(
        (account) => account.currency === this.$CURRENCY.GHS
      )
    },
    hasXAFPayout () {
      return this.totalAccounts.find(
        (account) => account.currency === this.$CURRENCY.XAF
      )
    },
    hasXOFPayout () {
      return this.totalAccounts.find(
        (account) => account.currency === this.$CURRENCY.XOF
      )
    },
    isNgnPayout () {
      return this.bulkPayoutForm.currency === this.$CURRENCY.NGN
    },
    PayoutCurrency () {
      const allowedCurrencies = FIAT.map((country) => country.currency)
      return this.totalAccounts.filter((account) =>
        allowedCurrencies.includes(account.currency)
      )
    },
    datePickerLabel () {
      switch (this.bulkPayoutForm.reccuring_period) {
        case 'monthly':
          return 'Day of the month:'
        case 'weekly':
          return 'Day of the week:'
        case 'daily':
          return 'Pick a day:'
        default:
          return 'Pick a date:'
      }
    },
    datePickerType () {
      if (this.bulkPayoutForm.reccuring_period === 'monthly') {
        return 'date'
      } else {
        return 'week'
      }
    },
    isRecurringPeriod () {
      return this.bulkPayoutForm.payout_frequency === 'recurring_payout'
    },
    formatHoldingsBalance () {
      return `${ParseMoneyAll(
        this.bulkPayoutForm.holdingBalance,
        this.bulkPayoutForm.currency
      )}`
    },
    sampleDocumentUrl () {
      return this.$store.state.bulkPayout.bulkPayoutConfig?.templateURL || ''
    },
    payoutConfig () {
      return this.$store.state.bulkPayout.bulkPayoutConfig
    },
    hasHoldingBalance () {
      return this.bulkPayoutForm.holdingBalance > 0
    },
    isDailyPayout () {
      return this.bulkPayoutForm.reccuring_period === 'daily'
    },
    isWeeklyPayout () {
      return this.bulkPayoutForm.reccuring_period === 'weekly'
    },
    isDailyPayoutTime () {
      const hours = this.bulkPayoutForm.time.getHours()
      const minutes = this.bulkPayoutForm.time.getMinutes()
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}`
    },
    selectedBeneficiary () {
      return this.$store.state.bulkPayout.beneficiaryDetails
    },
    selectedBeneficiaries () {
      return this.$store.state.bulkPayout.selectedBeneficiaries || []
    },
    formDetails () {
      return this.$store.state.bulkPayout.bulkPayoutForm || {}
    },
    accountProviderTitle () {
      return ACCOUNT_DETAILS_TITLES[
        this.bulkPayoutForm.paymentMethod || 'banks'
      ].provider
    },
    accountTitle () {
      return ACCOUNT_DETAILS_TITLES[
        this.bulkPayoutForm.paymentMethod || 'banks'
      ].account
    },
    isDevEnvironment () {
      // dev environment indicator
      return process.env.VUE_APP_DEV_INDICATOR === 'true'
    },
    isProceedButtonDisabled () {
      return (
        !this.hasHoldingBalance ||
        !this.hasInitiatorAccess ||
        this.isLoadingFileUpload
      )
    },
    isSingleBeneficiary () {
      return this.bulkPayoutForm.beneficiary_type === 'single'
    },
    isMultipleBeneficiaries () {
      return this.bulkPayoutForm.beneficiary_type === 'multiple'
    },
    uploadedFileUrls () {
      return this.$store.state.bulkPayout.uploadedFileUrls
    },
    firstUploadedFileType () {
      return this.uploadedFileUrls?.[0]?.split('.')?.pop() || ''
    }
  },
  methods: {
    selectedFileType (file) {
      // pick file type
      return file.name.match(/[^.]+$/)[0]
    },
    selectedFileSize (file) {
      // compute uploaded file size in KB
      return (file.size / 1024).toFixed(2) + ' KB'
    },
    handleBeneficiaryTypeChange () {
      this.removeSelectedInvoice()
    },
    async handleUploadFile () {
      this.isLoadingFileUpload = true
      let isSuccessfulFileUpload = false
      // Merge the new files into the existing file list
      this.selectedFilesToUpload = [...this.fileSelector.files]
      const formData = new FormData()
      this.selectedFilesToUpload.forEach((file) => {
        formData.append('files', file)
      })

      // Dispatch the action to upload the files
      await this.$store
        .dispatch('bulkPayout/uploadFile', {
          type: 'payout',
          data: formData
        })
        .then(() => {
          this.selectedFilesToUpload = [] // reset selectedFilesToUpload
          isSuccessfulFileUpload = true
          this.isLoadingFileUpload = false
        })
        .catch(() => {
          isSuccessfulFileUpload = false
          this.isLoadingFileUpload = false
        })
        .finally(() => {
          this.isLoadingFileUpload = false
        })
      return isSuccessfulFileUpload
    },
    disablePayoutForm (account) {
      return (
        !this.PayoutCurrency.includes(account) || !(account.currentBalance > 0)
      )
    },
    validateAmount (rule, value, callback) {
      if (FormatAmount(value) > this.bulkPayoutForm.holdingBalance) {
        callback(new Error("That's more than your balance"))
      } else if (!FormatAmount(value) || FormatAmount(value) < 1) {
        callback(new Error('Provide an amount'))
      } else {
        callback()
      }
    },
    handleInput () {
      this.bulkPayoutForm.amount = RemoveNonDigitAndDecimal(
        this.bulkPayoutForm.amount
      )
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    toggleUploadType () {
      this.showUploadList = !this.showUploadList
    },
    goToSelectBeneficiary () {
      this.$store.dispatch('bulkPayout/setBulkPayoutForm', this.bulkPayoutForm)
      this.$router.push('/payout/select-beneficiary')
    },
    selectBeneficiary (beneficiary) {
      this.selectedBeneficiary = beneficiary
    },
    clearSelectedBeneficiary () {
      this.$store.dispatch('bulkPayout/setBeneficiaryDetails', null)
    },
    updateHoldingBalance (newCurrency) {
      const selectedAccount = this.totalAccounts.find(
        (account) => account.currency === newCurrency
      )
      if (selectedAccount) {
        this.bulkPayoutForm.holdingBalance = selectedAccount.currentBalance
      } else {
        this.bulkPayoutForm.holdingBalance = 0
      }
    },
    handlePeriodChange (selectedPeriod) {
      this.bulkPayoutForm.selectedDate = ''
      this.bulkPayoutForm.reccuring_period = selectedPeriod
    },
    async handlePaymentMethod () {
      await this.getBulkPayoutConfig()
      this.$store.dispatch(
        'bulkPayout/savePaymentMethod',
        this.bulkPayoutForm.paymentMethod
      )
      this.handleHub2ProviderPayout({
        payoutMethod: this.bulkPayoutForm.paymentMethod,
        currency: this.bulkPayoutForm.currency
      })
    },
    async handleCurrencyChange () {
      this.clearSelectedBeneficiary()
      this.clearSavedBeneficiaries()
      // this condition is to cater for situations where a user had selected ghs and mobile money type.... but decides to change it to ngn ...
      //  So it sets the paymentMethod to an empty string at change
      if (
        this.bulkPayoutForm.currency === this.$CURRENCY.NGN ||
        this.bulkPayoutForm.currency === this.$CURRENCY.USD
      ) {
        this.bulkPayoutForm.paymentMethod = ''
      }
      // this is to cater for when we clear the user's previous selection but to get the document template for XAF and XOF, we can't pass empty string
      // BE is expecting mno
      if (
        this.bulkPayoutForm.currency === this.$CURRENCY.XAF ||
        this.bulkPayoutForm.currency === this.$CURRENCY.XOF
      ) {
        this.bulkPayoutForm.paymentMethod = 'mno'
        this.handleHub2ProviderPayout({
          payoutMethod: this.bulkPayoutForm.paymentMethod,
          currency: this.bulkPayoutForm.currency
        })
      }
      this.$store.dispatch(
        'bulkPayout/savePayoutCurrency',
        this.bulkPayoutForm.currency
      )
      await this.getBulkPayoutConfig()
    },
    parseXlsxSheet () {
      const fileInput = document.getElementById('fileInput')
      const file = fileInput.files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result)
        const workbook = read(data, { type: 'array' })
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const jsonData = sheet_to_json(worksheet, { header: 1 })
        // Process the parsed data
        this.filteredData = jsonData.filter((row) =>
          row.some((cell) => cell !== null && cell !== '')
        )
        this.parseXlsxSheetData()
      }
      reader.readAsArrayBuffer(file)
    },
    parseXlsxSheetData () {
      const labels = this.filteredData[0]
      const isUsd = this.bulkPayoutForm.currency === this.$CURRENCY.USD
      const bankNameIndex = labels.findIndex((x) => x === 'bank_name')
      const bankAddressIndex = labels.findIndex((x) =>
        x === isUsd ? 'address' : 'bank_address'
      )
      const swiftCodeIndex = labels.findIndex((x) => x === 'swift_code')
      const routingNoIndex = labels.findIndex((x) => x === 'routing_number')
      const postalCode = labels.findIndex((x) => x === 'postal_code')
      const accName = labels.findIndex((x) => x === 'account_name')
      const accountNumber = labels.findIndex((x) => x === 'account_number')
      const amountIndex = labels.findIndex((x) => x === 'amount')
      const remarksIndex = labels.findIndex((x) => x === 'remarks')
      const cityIndex = labels.findIndex((x) =>
        x === isUsd ? 'city' : 'bank_city'
      )
      const countryIndex = labels.findIndex((x) => x === 'country_code')

      // intermediary bank details
      const intermediaryBankNameIndex = labels.findIndex(
        (x) => x === 'intermediary_bank'
      )
      const intermediarySwiftCodeIndex = labels.findIndex(
        (x) => x === 'swift_code'
      )
      const intermediaryRoutingNoIndex = labels.findIndex(
        (x) => x === 'routing_number'
      )
      const intermediaryAddressIndex = labels.findIndex((x) => x === 'address')

      this.parsedXlsxSheetData = this.filteredData.slice(1).map((data) => {
        return {
          id: '',
          businessID: '',
          bulkPayoutID: '',
          name: '',
          details: {
            city: data[cityIndex] ? data[cityIndex] : '',
            country: data[countryIndex] ? this.filteredData[countryIndex] : '',
            bankCode: this.isNgnPayout ? this.addZeroPrefix(data[3], 3) : '',
            bankName: data[bankNameIndex] ? data[bankNameIndex] : '',
            district: '',
            swiftCode: data[swiftCodeIndex] ? data[swiftCodeIndex] : '',
            bankBranch: '',
            isWithinUS: '',
            postalCode: data[postalCode] ? data[postalCode] : '',
            bankAddress: data[bankAddressIndex] ? data[bankAddressIndex] : '',
            accountName: data[accName] ? data[accName] : '',
            accountNumber: data[accountNumber] ? data[accountNumber] : '',
            routingNumber: data[routingNoIndex] ? data[routingNoIndex] : '',
            intermediaryBank: data[swiftCodeIndex]
              ? {
                  swiftCode:
                    data[intermediarySwiftCodeIndex] ||
                    data[intermediaryRoutingNoIndex] ||
                    '',
                  bankAddress: data[intermediaryAddressIndex] || '',
                  bankName: data[intermediaryBankNameIndex] || ''
                }
              : null
          },
          amount: {
            currency: this.bulkPayoutForm.currency,
            symbol: '',
            amount: data[amountIndex] ? data[amountIndex] : ''
          },
          status: '-',
          provider: '',
          lookupInfo: '',
          remarks: data[remarksIndex] ? data[remarksIndex] : '',
          notes: null,
          createdAt: '',
          updatedAt: ''
        }
      })
      setTimeout(() => {
        this.getTotalAmount()

        let feeVal
        const feeFlat = this.payoutConfig?.feeFlat || 0
        const feePercentage = this.payoutConfig?.feePercentage || 0
        const feeCap = this.payoutConfig?.feeCap || 0

        if (feeFlat !== 0) {
          feeVal = feeFlat
        } else {
          feeVal = (feePercentage / 100) * this.totalAmountSum
        }

        if (feeCap !== 0 && feeVal > feeCap) {
          feeVal = feeCap
        }

        const payload = {
          items: {
            id: '',
            businessID: '',
            beneficiaryType: this.bulkPayoutForm.beneficiary_type,
            status: '-',
            count: this.parsedXlsxSheetData.length,
            currency: this.bulkPayoutForm.currency,
            totalAmount: this.totalAmountSum,
            fee: {
              currency: this.bulkPayoutForm.currency,
              symbol: '',
              amount: feeVal
            },
            remarks: this.bulkPayoutForm.remarks,
            isRecurring: this.isRecurringPeriod,
            cancelReason: null,
            completedAt: null,
            createdAt: '',
            updatedAt: ''
          },
          page: {
            page: 1,
            size: 10,
            hasNextPage: false,
            hasPreviousPage: false,
            totalCount: 0
          },
          attributes: this.parsedXlsxSheetData
        }
        this.$store
          .dispatch('bulkPayout/setCurrentBulkPayoutDetails', payload)
          .then(() => this.validationHandler('/payout/create-payout'))
      }, 300)
    },
    handleMultiBeneficiaries () {
      const totalAmount = this.selectedBeneficiaries
        .map((item) => item.amount)
        .reduce((a, b) => a + b, 0)

      let feeVal
      const feeFlat = this.payoutConfig?.feeFlat || 0
      const feePercentage = this.payoutConfig?.feePercentage || 0
      const feeCap = this.payoutConfig?.feeCap || 0

      if (feeFlat !== 0) {
        feeVal = feeFlat
      } else {
        feeVal = (feePercentage / 100) * totalAmount
      }
      if (feeCap !== 0 && feeVal > feeCap) {
        feeVal = feeCap
      }

      const mappedBeneficiaries = this.selectedBeneficiaries.map((item) => ({
        id: item.id,
        businessID: item.businessID,
        bulkPayoutID: '',
        name: item.name,
        details: {
          city: item.details.bankDetails.city || '',
          country: item.details.bankDetails.country || '',
          bankCode: item.details.bankDetails.bankCode || '',
          bankName: item.details.bankDetails.bankName || '',
          district: item.details.bankDetails.district || '',
          swiftCode: item.details.bankDetails.swiftCode || '',
          bankBranch: item.details.bankDetails.bankBranch || '',
          isWithinUS: item.details.bankDetails.isWithinUS || '',
          postalCode: item.details.bankDetails.postalCode || '',
          bankAddress: item.details.bankDetails.bankAddress || '',
          accountName: item.details.bankDetails.accountName || '',
          accountNumber: item.details.bankDetails.accountNumber || '',
          routingNumber: item.details.bankDetails.routingNumber || '',
          intermediaryBank: item.details.intermediaryBank
            ? {
                swiftCode:
                  item.details.intermediaryBank.swiftCode ||
                  item.details.intermediaryBank.routingNumber ||
                  '',
                bankAddress: item.details.intermediaryBank.bankAddress || '',
                bankName: item.details.intermediaryBank.bankName || ''
              }
            : null
        },
        amount: {
          currency: this.bulkPayoutForm.currency,
          symbol: '',
          amount: item.amount || ''
        },
        status: '-',
        provider: '',
        lookupInfo: '',
        remarks: '',
        notes: null,
        createdAt: '',
        updatedAt: ''
      }))

      const payload = {
        items: {
          id: '',
          businessID: '',
          beneficiaryType: this.bulkPayoutForm.beneficiary_type,
          status: '-',
          count: this.selectedBeneficiaries.length,
          currency: this.bulkPayoutForm.currency,
          totalAmount: totalAmount,
          fee: {
            currency: this.bulkPayoutForm.currency,
            symbol: '',
            amount: feeVal
          },
          payoutType: this.payoutType,
          remarks: this.bulkPayoutForm.remarks,
          isRecurring: this.isRecurringPeriod,
          cancelReason: null,
          completedAt: null,
          createdAt: '',
          updatedAt: '',
          scheduleDetails: {}
        },
        page: {
          page: 1,
          size: 10,
          hasNextPage: false,
          hasPreviousPage: false,
          totalCount: 0
        },
        attributes: mappedBeneficiaries
      }
      if (this.isRecurringPeriod) {
        this.updateRecurringScheduleDetails()
        payload.items.scheduleDetails = { ...this.scheduleDetails }
      }
      this.$store
        .dispatch('bulkPayout/setCurrentBulkPayoutDetails', payload)
        .then(() => this.validationHandler('/payout/create-payout'))
    },
    getTotalAmount () {
      this.parsedXlsxSheetData.forEach((s) => {
        this.totalAmountSum += s.amount.amount
      })
    },
    handleChange () {
      const fileInput = document.getElementById('fileInput')
      const file = fileInput.files[0]
      if (file) {
        this.fileName = file.name
      } else {
        this.fileName = ''
      }
    },
    handleInvoiceChange () {
      const fileInput = document.getElementById('invoiceInput')
      this.fileSelector = fileInput
      const file = fileInput.files[0]
      if (file) {
        this.invoiceFileName = file.name
      } else {
        this.invoiceFileName = ''
      }
    },
    setUploadedInvoiceToStorage () {},
    appendRecurringFormData () {
      this.formData.append('isRecurring', this.isRecurringPeriod)
      switch (this.bulkPayoutForm.reccuring_period) {
        case 'monthly':
          this.formData.append(
            'dayOfTheWeek',
            this.bulkPayoutForm.selectedDate.getDay()
          )
          this.formData.append(
            'dayOfTheMonth',
            this.bulkPayoutForm.selectedDate.getDate()
          )
          break
        case 'weekly':
          this.formData.append('dayOfTheWeek', this.bulkPayoutForm.selectedDate)
          break
      }
      this.formData.append('frequency', this.bulkPayoutForm.reccuring_period)
      if (this.hasPayoutEndDate) {
        // the method is to get the date string in UTC format and then extract the "yyyy-mm-dd"... the backend expects the payout date in the format 2021-09-22
        this.formData.append(
          'endDate',
          this.payoutEndDateObject.toISOString().split('T')[0]
        )
      }
    },
    updateRecurringScheduleDetails () {
      switch (this.bulkPayoutForm.reccuring_period) {
        case 'monthly':
          this.scheduleDetails.dayOfTheMonth =
            this.bulkPayoutForm.selectedDate.getDate()
          break
        case 'weekly':
          this.scheduleDetails.dayOfTheWeek = this.bulkPayoutForm.selectedDate
          break
      }
      this.scheduleDetails.frequency = this.bulkPayoutForm.reccuring_period
      if (this.hasPayoutEndDate) {
        // the method is to get the date string in UTC format and then extract the "yyyy-mm-dd"... the backend expects the payout date in the format 2021-09-22
        this.scheduleDetails.endDate = this.payoutEndDateObject
          .toISOString()
          .split('T')[0]
      }
    },
    convertToFormData () {
      const fileInput = document.getElementById('fileInput')
      const file = fileInput.files[0]
      if (file) {
        this.fileName = file.name
        this.formData = new FormData()
        this.formData.append('document', file)
        this.formData.append('currency', this.bulkPayoutForm.currency)
        this.formData.append(
          'beneficiaryType',
          this.bulkPayoutForm.beneficiary_type
        )
        this.formData.append('remarks', this.bulkPayoutForm.remarks)
        this.formData.append('payoutType', this.payoutType)
        this.formData.append(
          'beneficiaryType',
          this.bulkPayoutForm.beneficiaryType
        )
        if (this.isRecurringPeriod) {
          this.appendRecurringFormData()
        }
        this.formData.append('invoices', JSON.stringify(this.uploadedFileUrls))
        this.$store.dispatch('bulkPayout/setBulkPayoutFormData', this.formData)
      }
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    addZeroPrefix (val, digitCount) {
      return AddZeroPrefix(val, digitCount)
    },
    closeModal (arg) {
      this[arg] = false
    },
    openModal (arg) {
      this[arg] = true
    },
    goBack () {
      this.$store.dispatch(
        'bulkPayout/setActiveComponent',
        'bulk-payout-overview'
      )
    },
    handleSinglePayout () {
      if (!this.selectedBeneficiary) {
        // if no selected beneficiary
        this.$notify.error({
          title: 'No beneficiary Selected',
          message: 'Please select a beneficiary',
          position: 'top-right'
        })
        return
      }
      let feeVal
      const feeFlat = this.payoutConfig?.feeFlat || 0
      const feePercentage = this.payoutConfig?.feePercentage || 0
      const feeCap = this.payoutConfig?.feeCap || 0
      if (feeFlat !== 0) {
        feeVal = feeFlat
      } else {
        feeVal =
          (feePercentage / 100) * FormatAmount(this.bulkPayoutForm.amount)
      }

      if (feeCap !== 0 && feeVal > feeCap) {
        feeVal = feeCap
      }
      const payoutDetails = {
        id: '',
        businessID: '',
        bulkPayoutID: '',
        name: '',
        details: {
          payoutType: this.bulkPayoutForm.paymentMethod || '',
          ...this.selectedBeneficiary
        },
        amount: {
          currency: this.bulkPayoutForm.currency,
          symbol: '',
          amount: FormatAmount(this.bulkPayoutForm.amount) || ''
        }
      }
      const payload = {
        items: {
          id: '',
          businessID: '',
          beneficiaryType: this.bulkPayoutForm.beneficiary_type,
          status: '-',
          currency: this.bulkPayoutForm.currency,
          totalAmount: FormatAmount(this.bulkPayoutForm.amount),
          fee: {
            currency: this.bulkPayoutForm.currency,
            symbol: '',
            amount: feeVal
          },
          remarks: this.bulkPayoutForm.remarks,
          isRecurring: this.isRecurringPeriod,
          cancelReason: null,
          completedAt: null,
          createdAt: '',
          updatedAt: '',
          scheduleDetails: {}
        },
        page: {
          page: 1,
          size: 10,
          hasNextPage: false,
          hasPreviousPage: false,
          totalCount: 0
        },
        attributes: payoutDetails
      }
      if (this.isRecurringPeriod) {
        this.updateRecurringScheduleDetails()
        payload.items.scheduleDetails = { ...this.scheduleDetails }
      }
      this.$store
        .dispatch('bulkPayout/setCurrentBulkPayoutDetails', payload)
        .then(() => {
          this.validationHandler('/payout/create-payout')
        })
    },
    async validateForm (form) {
      this.$refs[form].validate(async (valid) => {
        if (!valid) {
          this.$notify.error({
            title: 'Invalid input(s)',
            message: 'Please fill all inputs correctly',
            position: 'top-right'
          })
          return
        }
        let isSuccessfulFileUpload = true
        if (this.bulkPayoutForm.invoice || this.uploadedFileUrls.length > 0) {
          // if invoice is not empty or invoice has not been uploaded
          isSuccessfulFileUpload = await this.handleUploadFile()
        }
        if (!isSuccessfulFileUpload) {
          this.$notify.error({
            title: 'Error',
            message: 'File upload was not successful',
            position: 'top-right'
          })
          return false
        }
        if (this.isMultipleBeneficiaries) {
          if (this.selectedBeneficiaries.length === 0) {
            this.parseXlsxSheet()
            this.convertToFormData()
          } else {
            this.handleMultiBeneficiaries()
          }
        } else if (this.isSingleBeneficiary) {
          this.handleSinglePayout()
        }
      })
    },
    findPayoutAccount () {
      if (this.hasNGNPayout) {
        this.bulkPayoutForm.currency = this.hasNGNPayout.currency
        this.bulkPayoutForm.holdingBalance = this.hasNGNPayout.currentBalance
      } else if (this.hasGHSPayout) {
        this.bulkPayoutForm.currency = this.hasGHSPayout.currency
        this.bulkPayoutForm.holdingBalance = this.hasGHSPayout.currentBalance
      } else if (this.hasXAFPayout) {
        this.bulkPayoutForm.currency = this.hasXAFPayout.currency
        this.bulkPayoutForm.holdingBalance = this.hasXAFPayout.currentBalance
      } else if (this.hasXOFPayout) {
        this.bulkPayoutForm.currency = this.hasXOFPayout.currency
        this.bulkPayoutForm.holdingBalance = this.hasXOFPayout.currentBalance
      } else {
        this.bulkPayoutForm.currency = this.$CURRENCY.NGN
        this.bulkPayoutForm.holdingBalance = 0
      }
    },
    shouldDisablePaymentMethod (paymentMethod) {
      const paymentType =
        paymentMethod.label === 'Bank Transfer'
          ? 'isBankTransferEnabled'
          : 'isMobileMoneyEnabled'
      const isEnabled = ENABLED_MULTI_CURRENCY_AND_METHODS.find(
        (enabledType) => enabledType.currency === this.bulkPayoutForm.currency
      )
      return !isEnabled[paymentType]
    },
    validationHandler (route) {
      this.showValidationDialog = true
      setTimeout(() => {
        this.showValidationDialog = false
        this.$router.push(route)
      }, 1000)
    },
    async getBulkPayoutConfig () {
      this.isLoading = true
      this.templateCurrency = this.bulkPayoutForm.currency
      const payload = {
        currency: this.templateCurrency,
        type: this.payoutType
      }
      await this.$store
        .dispatch('bulkPayout/getBulkPayoutConfig', payload)
        .then(() => {})
        .catch((error) => {
          this.isLoading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    payoutTypeDisabled (payoutType) {
      return (
        this.bulkPayoutForm.currency === this.$CURRENCY.USD &&
        payoutType === 'recurring_payout'
      )
    },
    clearSavedBeneficiaries () {
      this.$store.dispatch('bulkPayout/removeSelectedBeneficiaries', [])
      this.bulkPayoutForm.beneficiary_type = ''
      this.bulkPayoutForm.paymentMethod = ''
    },
    prefillForm () {
      this.bulkPayoutForm = {
        amount: this.formDetails.amount || '',
        currency: this.formDetails.currency || '',
        beneficiary_type: this.formDetails.beneficiary_type || '',
        holdingBalance: this.formDetails.holdingBalance || '',
        document: this.formDetails.document || '',
        remarks: this.formDetails.remarks || '',
        payout_frequency: this.formDetails.payout_frequency || '',
        reccuring_period: this.formDetails.reccuring_period || '',
        selectedDate: this.formDetails.selectedDate || '',
        paymentMethod: this.formDetails.paymentMethod || '',
        payoutEndDate: this.formDetails.payoutEndDate || '',
        beneficiaryType: this.formDetails.beneficiaryType || ''
      }
    },
    resetUploadedFileUrls () {
      this.$store.dispatch('bulkPayout/resetUploadedFileUrls')
    },
    removeSelectedInvoice (invoiceIndex = null) {
      if (invoiceIndex === null) {
        // Reset these fields if no index is provided
        this.fileSelector = null
        this.invoiceFileName = ''
        this.bulkPayoutForm.invoice = '' // Set it as an empty string or a default value
        this.resetUploadedFileUrls()
      } else if (
        this.fileSelector &&
        this.fileSelector.files &&
        invoiceIndex >= 0 &&
        invoiceIndex < this.fileSelector.files.length
      ) {
        // Convert the FileList to an array
        const filesArray = Array.from(this.fileSelector.files)

        // Remove the item at the specified index
        filesArray.splice(invoiceIndex, 1)

        // Create a new DataTransfer object to hold the modified files
        const dataTransfer = new DataTransfer()
        filesArray.forEach((file) => dataTransfer.items.add(file))

        // Reassign the modified FileList back to fileSelector.files
        this.fileSelector.files = dataTransfer.files

        // Update bulkPayoutForm.invoice
        if (filesArray.length > 0) {
          this.bulkPayoutForm.invoice = filesArray
            .map((file) => file.name)
            .join(', ')
        } else {
          this.fileSelector = null
          this.invoiceFileName = ''
          this.bulkPayoutForm.invoice = '' // No files left
          this.resetUploadedFileUrls()
        }
      }
    }
  },
  watch: {
    'bulkPayoutForm.currency': {
      immediate: true,
      handler (newCurrency) {
        if (this.selectedBeneficiaries.length === 0) {
          this.bulkPayoutForm.amount = ''
          this.updateHoldingBalance(newCurrency)
        }
      }
    },
    'bulkPayoutForm.amount': function (newValue) {
      if (this.selectedBeneficiaries.length === 0) {
        // const result = ParseAmount(newValue)
        // this.bulkPayoutForm.amount = `${
        //   GetCurrencyDetails(this.bulkPayoutForm.currency).symbol
        // }${result}`
        let pattern = /[^\d.]/g
        if (this.isHub2Provider) {
          // if payout partner is hub2, prevent decimal input
          pattern = /[^\d]/g
        }
        const result = newValue.replace(pattern, '')
        this.$nextTick(() => {
          this.bulkPayoutForm.amount = `${
            GetCurrencyDetails(this.bulkPayoutForm.currency).symbol
          }${ParseAmount(result)}`
        })
      }
    },
    deep: true
  },
  async mounted () {
    this.findPayoutAccount()
    if (this.selectedBeneficiaries.length > 0) {
      this.prefillForm()
    }
    this.$store.dispatch(
      'bulkPayout/savePayoutCurrency',
      this.bulkPayoutForm.currency
    )
    await this.getBulkPayoutConfig()
  }
}
</script>

<style scoped>
* >>> .el-upload-list {
  position: absolute;
  left: 0;
  top: 0;
  width: 15rem;
  height: 0.8rem;
}
* >>> .el-upload-list__item-name {
  font-size: 0.7rem;
}
.close {
  width: 16px !important;
  height: 16px !important;
}

.backgroundColor {
  background-color: var(--accent-disabled);
}

* >>> .el-select {
  width: 100% !important;
}

* >>> .el-date-editor.el-input,
.el-date-editor.el-input__inner {
  width: 100% !important;
}

* >>> .el-icon-date {
  display: none;
}

* >>> .el-icon-time {
  display: none;
}

* >>> .el-input__inner {
  border-radius: 0.6rem;
  height: 3.5rem;
}

* >>> #upload_beneficiary_list.el-input__inner,
* >>> #select.el-input__inner,
* >>> #holding_balance.el_input__inner {
  color: transparent !important;
}

* >>> .el-input__inner:hover,
* >>> .el-input__inner:focus,
* >>> .el-input__inner:active {
  border-color: #a5a7b6;
}
.el-upload {
  border: 1px solid #a5a7b6;
  border-radius: 0.6rem;
  height: 3.5rem;
}
* >>> .el-form-item__error {
  font-weight: bold;
}

* >>> .el-select-dropdown__item {
  padding: 1rem 2rem !important;
}

* >>> input#fileInput.el-input__inner,
* >>> input#invoiceInput.el-input__inner {
  color: transparent;
}

* >>> .el-input__inner::file-selector-button {
  background: transparent;
  color: transparent;
  border: none;
  position: relative;
  width: 100% !important;
  z-index: 20 !important;
  cursor: pointer;
}

.browseButton {
  font-style: normal !important;
  font-weight: 400;
}

@media screen and (max-width: 768px) {
  * >>> .el-form-item__label,
  .small-text,
  .browseButton,
  .el-input__inner {
    font-size: 0.75rem;
  }
}
.upload-invoice-label {
  font-size: 0.8rem;
  color: #616161;
}
</style>
